import { createContext, ReactNode, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";

/** A nextgen project that the pipeline views are scoped to instead of a workbench. */
export interface ProjectPipelineScope {
  /** The project's UUID, which the Pipelines API scopes requests by. */
  projectId: string;
  /** Shown where the workbench views show the workbench title. */
  projectName: string;
  /** Router path the pipeline views are mounted under, in place of `/workbenches/<id>`. */
  appBasePath: string;
  /** Router path of the project's files view, for links to files in the project. */
  filesBasePath: string;
}

export type PipelineScope =
  | { kind: "workbench"; workbenchId: string | undefined }
  | ({ kind: "project" } & ProjectPipelineScope);

const PipelineScopeContext = createContext<ProjectPipelineScope | null>(null);

/** Scopes the pipeline views below it to a project rather than the route's workbench. */
export const PipelineScopeProvider = ({
  value,
  children,
}: {
  value: ProjectPipelineScope;
  children: ReactNode;
}) => <PipelineScopeContext.Provider value={value}>{children}</PipelineScopeContext.Provider>;

/** The project the pipeline views are scoped to, or null for the route's workbench. */
export const useProjectPipelineScope = () => useContext(PipelineScopeContext);

/**
 * The scope pipeline requests are made in: the project from `PipelineScopeProvider`
 * when one is set, otherwise the workbench from the route's `:projectId`.
 */
export const usePipelineScope = (): PipelineScope => {
  const project = useContext(PipelineScopeContext);
  const { projectId: workbenchId } = useParams();
  return useMemo(
    () => (project ? { kind: "project", ...project } : { kind: "workbench", workbenchId }),
    [project, workbenchId]
  );
};

/** The workbench id or project id, or undefined when the route names no workbench. */
export const pipelineScopeId = (scope: PipelineScope): string | undefined =>
  scope.kind === "project" ? scope.projectId : scope.workbenchId;

/** The Pipelines API header that names the scope. */
export const pipelineScopeHeaders = (scope: PipelineScope): Record<string, string> =>
  scope.kind === "project"
    ? { "x-sc-project-id": scope.projectId }
    : { "x-sc-workbench-id": scope.workbenchId ?? "" };

/** Adds the Pipelines API query parameter that names the scope, when there is one. */
export const setPipelineScopeQueryParam = (params: URLSearchParams, scope: PipelineScope) => {
  if (scope.kind === "project") {
    params.set("project_id", scope.projectId);
  } else if (scope.workbenchId) {
    params.set("workbench_id", scope.workbenchId);
  }
};

export type PipelineScopeEventProperties =
  | { scope_type: "workbench"; workbench_id: string | undefined }
  | { scope_type: "project"; project_id: string };

/** The analytics properties that identify the scope. */
export const pipelineScopeEventProperties = (scope: PipelineScope): PipelineScopeEventProperties =>
  scope.kind === "project"
    ? { scope_type: "project", project_id: scope.projectId }
    : { scope_type: "workbench", workbench_id: scope.workbenchId };
